import React, { useEffect, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import HighchartsMap from "highcharts/modules/map";
import mapDataWorld from "@highcharts/map-collection/custom/world.geo.json";
import {
  Box,
  List,
  ListItem,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import {
  getCountryKeyByCode,
  getCountryNameFromCode,
} from "../../utils/countryLabels";

// Initialize the map module
HighchartsMap(Highcharts);

const WorldHeatmap = ({ heatMapData }) => {
  const [chartData, setChartData] = useState([]);
  const [open, setOpen] = useState(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  useEffect(() => {
    if (heatMapData?.length) {
      const formattedData = heatMapData
        .map((item) => ({
          "hc-key": getCountryKeyByCode(item.country),
          countryCode: item.country,
          value: item.value,
        }))
        .filter((item) => item["hc-key"]);

      setChartData(formattedData);
    } else {
      setChartData([]);
    }
  }, [heatMapData]);

  const options = {
    chart: {
      map: mapDataWorld,
      // backgroundColor: '#0000FF', // Set blue background to match the reference
      height: 300, // Increase the height of the chart
    },
    title: {
      text: "Heatmap of Location of Guests",
      style: { color: "#FFFFFF" },
    },
    credits: { enabled: false },
    colorAxis: {
      min: 0,
      max: 100,
      stops: [
        [0, "#0000FF"], // Blue
        [0.4, "#00FF00"], // Green
        [0.6, "#FFFF00"], // Yellow
        [0.8, "#FFA500"], // Orange
        [1, "#FF0000"], // Red
      ],
    },
    mapNavigation: {
      enabled: false,
    },
    plotOptions: {
      map: {
        borderWidth: 0,
        states: {
          hover: {
            enabled: false,
          },
        },
      },
    },
    tooltip: {
      enabled: true, // Enable the tooltip
      formatter: function () {
        const countryName = this.point.name || "Unknown Country";
        const value = this.point.value || 0;
        return `<b>${countryName}</b>: ${value}`; // Custom tooltip content
      },
    },
    series: [
      {
        // The heatmap layer
        name: "Guest Density Heatmap",
        mapData: mapDataWorld,
        type: "map",
        data: chartData.length > 0 ? chartData : [],
        joinBy: ["hc-key"],
        // nullColor: '#0000FF', // Ensures areas with no data appear blue
      },
    ],
  };

  // sort data by value desc
  const sortedData = [...chartData].sort((a, b) => b.value - a.value);
  const top5 = sortedData.slice(0, 5);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: "#f8fbfd",
        height: "100%",
      }}
    >
      {/* Heatmap chart */}
      <Box
        width="100%"
        sx={{ boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)", borderRadius: 2 }}
      >
        <HighchartsReact
          highcharts={Highcharts}
          constructorType="mapChart"
          options={options}
        />
      </Box>

      {/* Country list */}
      <Box
        sx={{
          maxHeight: 400,
          px: 2,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Typography
          variant="subtitle1"
          color="primary"
          gutterBottom
          sx={{ flexShrink: 0 }}
        >
          Countries:
        </Typography>

        {sortedData.length > 0 ? (
          <Box
            sx={{
              overflowY: "auto",
              flexGrow: 1,
              borderRadius: 2,
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
              p: 2,
              bgcolor: "#fff",
            }}
          >
            {/* Top 5 countries */}
            <List dense disablePadding>
              {top5.map((dataItem, index) => {
                const countryName =
                  getCountryNameFromCode(dataItem.countryCode) ||
                  "Unknown Country";

                return (
                  <ListItem
                    key={`top-${index}`}
                    sx={{
                      py: 1,
                      borderBottom:
                        index !== top5.length - 1
                          ? "1px solid #f0f0f0"
                          : "none",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "14px",
                        display: "flex",
                        justifyContent: "space-between",
                        width: "100%",
                        letterSpacing: 0.2,
                        "& span": {
                          fontWeight: 600,
                          color: "#333",
                          ml: 3,
                          minWidth: 40,
                          textAlign: "right",
                        },
                      }}
                    >
                      {countryName} <span>{dataItem.value}</span>
                    </Typography>
                  </ListItem>
                );
              })}
            </List>

            {/* Label and arrow */}
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              mt={2}
              sx={{ cursor: "pointer" }}
              onClick={handleOpen}
            >
              <Typography variant="body2" color="#1186c0" fontWeight="bold">
                Where visitors come from
              </Typography>
              <IconButton size="small" sx={{ ml: 0.5 }}>
                <ArrowForwardIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No country to display
          </Typography>
        )}
      </Box>

      {/* Popup for all countries */}
      <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
        <DialogTitle>All Countries</DialogTitle>
        <DialogContent dividers>
          {sortedData.length > 0 ? (
            <List dense disablePadding>
              {sortedData.map((dataItem, index) => {
                const countryName =
                  getCountryNameFromCode(dataItem.countryCode) ||
                  "Unknown Country";

                return (
                  <ListItem
                    key={`all-${index}`}
                    sx={{
                      py: 1,
                      borderBottom:
                        index !== sortedData.length - 1
                          ? "1px solid #f0f0f0"
                          : "none",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "14px",
                        display: "flex",
                        justifyContent: "space-between",
                        width: "100%",
                        letterSpacing: 0.2,
                        "& span": {
                          fontWeight: 600,
                          color: "#333",
                          ml: 3,
                          minWidth: 40,
                          textAlign: "right",
                        },
                      }}
                    >
                      {countryName} <span>{dataItem.value}</span>
                    </Typography>
                  </ListItem>
                );
              })}
            </List>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No country to display
            </Typography>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default WorldHeatmap;
